import React, { useState } from 'react';
import { Loader2, BookOpen, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

/**
 * Auto-Catalog PDF Generator dialog.
 *
 * Admin types a keyword (e.g. "Haargummis") or filters and gets back a styled
 * branded PDF catalog of all matching products. The PDF is generated server-side
 * via `/api/catalog-pdf/generate`.
 */
const CatalogDialog = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [layout, setLayout] = useState('card'); // 'card' | 'grid'
  const [cover, setCover] = useState(true);
  const [handmadeOnly, setHandmadeOnly] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (title.trim()) params.set('title', title.trim());
      if (intro.trim()) params.set('intro', intro.trim());
      params.set('layout', layout);
      params.set('cover', cover ? 'true' : 'false');
      if (handmadeOnly) params.set('handmade_only', 'true');

      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/catalog-pdf/generate?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Keine Produkte gefunden');
        return;
      }
      const blob = await res.blob();
      // Trigger download
      const link = document.createElement('a');
      const dlUrl = URL.createObjectURL(blob);
      link.href = dlUrl;
      const slug = (query || 'alle').replace(/[^a-z0-9-_]/gi, '_').toLowerCase();
      link.download = `homeyoursoul-katalog-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success('Katalog erstellt');
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Erstellen des Katalogs');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    // Open in a new tab — same params, just open instead of download
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (title.trim()) params.set('title', title.trim());
    if (intro.trim()) params.set('intro', intro.trim());
    params.set('layout', layout);
    params.set('cover', cover ? 'true' : 'false');
    if (handmadeOnly) params.set('handmade_only', 'true');

    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/catalog-pdf/generate?${params.toString()}`;
    // Fetch as blob and open in new tab so the Authorization header is sent
    setGenerating(true);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Keine Produkte gefunden');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
    } catch (err) {
      console.error(err);
      toast.error('Fehler bei der Vorschau');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#5D4E3C]">
            <BookOpen className="h-5 w-5" />
            Katalog erstellen
          </DialogTitle>
          <DialogDescription>
            Tippe ein Stichwort (z.B. &bdquo;Haargummis&ldquo;) — alle passenden Produkte werden automatisch in einem PDF-Katalog gruppiert.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="catalog-query" className="text-[#5D4E3C] font-semibold">
              Stichwort / Filter
            </Label>
            <Input
              id="catalog-query"
              data-testid="catalog-query-input"
              placeholder="z.B. Haargummis, Kerzen, Tischdeko …"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <p className="text-[11px] text-[#9C8C7A] mt-1">
              Sucht in Name, Kategorie, Beschreibung, SKU und Tags. Leer = alle Produkte.
            </p>
          </div>

          <div>
            <Label htmlFor="catalog-title" className="text-[#5D4E3C] font-semibold">
              Titel auf Cover (optional)
            </Label>
            <Input
              id="catalog-title"
              data-testid="catalog-title-input"
              placeholder="z.B. Haargummi-Kollektion 2026"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="catalog-intro" className="text-[#5D4E3C] font-semibold">
              Intro-Text (optional)
            </Label>
            <Textarea
              id="catalog-intro"
              data-testid="catalog-intro-input"
              placeholder="Eine kurze Beschreibung für die Cover-Seite …"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={2}
            />
          </div>

          <div>
            <Label className="text-[#5D4E3C] font-semibold block mb-2">Layout</Label>
            <div className="inline-flex border border-[#C4A084] overflow-hidden">
              <button
                type="button"
                onClick={() => setLayout('card')}
                data-testid="catalog-layout-card"
                className={`px-4 py-2 text-sm transition-colors ${
                  layout === 'card' ? 'bg-[#5D4E3C] text-white' : 'bg-white text-[#5D4E3C]'
                }`}
              >
                Karten (1 pro Reihe, mit Beschreibung)
              </button>
              <button
                type="button"
                onClick={() => setLayout('grid')}
                data-testid="catalog-layout-grid"
                className={`px-4 py-2 text-sm transition-colors ${
                  layout === 'grid' ? 'bg-[#5D4E3C] text-white' : 'bg-white text-[#5D4E3C]'
                }`}
              >
                Grid (2 pro Reihe, kompakt)
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between bg-[#FAF5EE] p-3">
            <div>
              <Label className="text-[#5D4E3C] font-semibold">Cover-Seite</Label>
              <p className="text-[11px] text-[#9C8C7A]">Logo · Titel · Datum als erste Seite</p>
            </div>
            <Switch
              checked={cover}
              onCheckedChange={setCover}
              data-testid="catalog-cover-switch"
            />
          </div>

          <div className="flex items-center justify-between bg-[#FAF5EE] p-3">
            <div>
              <Label className="text-[#5D4E3C] font-semibold">Nur Handgemachtes</Label>
              <p className="text-[11px] text-[#9C8C7A]">Filter &bdquo;Handmade&ldquo; auf alle Treffer anwenden</p>
            </div>
            <Switch
              checked={handmadeOnly}
              onCheckedChange={setHandmadeOnly}
              data-testid="catalog-handmade-switch"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={generating}
            data-testid="catalog-cancel"
          >
            Abbrechen
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
            onClick={handlePreview}
            disabled={generating}
            data-testid="catalog-preview"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Vorschau
          </Button>
          <Button
            className="flex-1 bg-[#5D4E3C] hover:bg-[#4A3E32] text-white"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="catalog-generate"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            PDF Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CatalogDialog;
